import '@common/testing/components/TefComsSpidSelect.js';
import { injectExternalResources } from '@common/lit/injectExternalResources.js';
import { TemplateTheme } from '@common/lit/TemplateResolver.js';
import { getBackendUri } from '@common/testing/getBackendUri.js';
import {
  createTestBearerToken, createTestPiranhaToken, isCorrectUrlForWebCrypto, isPiranhaToken,
} from '@common/testing/index.js';
import { config } from '@src/config/index.js';
import { components } from '@src/testpage/components.js';
import type { TefComs } from '@src/types/typings.d.js';

const { createApp, defineComponent } = Vue;

const API_DIRECT_DOMAIN = 'api-4p.high.coms-dev.aws.de.pri.o2.com';

const shortcuts = {
  apiUrlGateway: [
    { value: 'https://ict.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'ict' },
    { value: 'https://e2e2.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e2' },
    { value: 'https://e2e1.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'e2e1' },
    { value: 'https://perf.high.apigw-test.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'nfr' },
    { value: 'https://prod.high.apigw-prod.aws.de.pri.o2.com/gateway/4p-extra-services/v1', label: 'prod' },
    { value: 'https://api-consent-coms.telefonica.de/gateway/4p-extra-services/v1', label: 'prod/extern' },
  ],
  apiUrlDirect: [
    { value: 'http://localhost:3200', label: 'api4p@localhost' },
    { value: 'http://localhost:8080', label: 'wiremock@localhost' },
    { value: `https://ict-${API_DIRECT_DOMAIN}`, label: 'ict' },
    { value: `https://e2e2-${API_DIRECT_DOMAIN}`, label: 'e2e2' },
    { value: `https://e2e1-${API_DIRECT_DOMAIN}`, label: 'e2e1' },
    { value: `https://nfr-${API_DIRECT_DOMAIN}`, label: 'nfr' },
  ],
  themeBaseUrl: [
    { value: 'https://dev-library.telefonica.de/1', label: 'occl/dev/o2', color: 'text-bg-primary' },
    { value: 'https://dev-library.telefonica.de/2', label: 'occl/dev/blau', color: 'text-bg-primary' },
    { value: 'https://library.telefonica.de/1', label: 'occl/prod/o2', color: 'text-bg-primary' },
    { value: 'https://library.telefonica.de/2', label: 'occl/prod/blau', color: 'text-bg-primary' },
  ],
  tefOne: [
    { value: 'alditalk', label: 'alditalk', color: 'text-bg-secondary' },
    { value: 'ayyildiz', label: 'Ayyildiz', color: 'text-bg-secondary' },
    { value: 'blau', label: 'Blau', color: 'text-bg-secondary' },
    { value: 'fonic', label: 'Fonic', color: 'text-bg-secondary' },
    { value: 'nettokom', label: 'NettoKOM', color: 'text-bg-secondary' },
    { value: 'o2', label: 'o2', color: 'text-bg-secondary' },
    { value: 'ortel', label: 'Ortel', color: 'text-bg-secondary' },
    { value: 'whatsappsim', label: 'WhatsApp SIM', color: 'text-bg-secondary' },
  ],
  tefOneEnv: [
    { value: 'main', label: 'main' },
    { value: 'staging', label: 'staging' },
  ],
  themes: [
    { value: 'occl', label: 'occl', color: 'text-bg-primary' },
    { value: 'tefOne', label: 'tefOne', color: 'text-bg-secondary' },
  ],
};

const environmentMatch = /(^.*)-micro-frontend-proxy/.exec(document.location.host);
if (environmentMatch) {
  const environment = environmentMatch[1];
  const finalApiUrl = `https://${environment}-${API_DIRECT_DOMAIN}`;
  if (!shortcuts.apiUrlDirect.map((u) => u.value).includes(finalApiUrl)) {
    shortcuts.apiUrlDirect.push({ value: finalApiUrl, label: environment });
  }
}

const search = new URLSearchParams(window.location.search);

const currentApiUrl = search.get('apiUrl')
  ?? getBackendUri({ backend: 'api-4p', gatewayContext: '/4p-extra-services{-env}/v1' });

const params = {
  accessToken: search.get('accessToken') ?? '',
  spId: search.get('spId') ?? 'SP901',
  lcId: search.get('lcId') ?? '4711',
  apiUrl: currentApiUrl,
  theme: search.get('theme') ?? 'occl',
  themeBaseUrl: search.get('themeBaseUrl') ?? 'https://dev-library.telefonica.de/1',
  tefOne: search.get('tefOne') ?? 'o2',
  tefOneEnv: search.get('tefOneEnv') ?? 'main',
  traceid: search.get('traceid') ?? '',
  mode: search.get('mode') ?? '',
};

interface CreateMicrofrontendOptions {
  accessToken?: string;
  spId: string;
  lcId: string;
  apiUrl: string;
  theme: string;
  themeBaseUrl?: string;
  tefOne?: string;
  tefOneEnv?: string;
  traceid?: string;
  mode?: string;
}

/**
 * Creates a microfrontend given the current configuration
 */
const createMicroFrontend = async ({
  accessToken = '',
  spId = '',
  lcId = '',
  apiUrl = '',
  theme = 'occl',
  themeBaseUrl = 'https://dev-library.telefonica.de/1',
  traceid = '',
  mode = '',
}: CreateMicrofrontendOptions) => {
  const mf = document.createElement(config.componentTagName);

  const setAttributes = (attrs: Record<string, string | undefined>) => Object
    .entries(attrs)
    .filter(([, value]) => value) // ignore '', false, undefined
    .forEach(([name, value]) => mf.setAttribute(name, value!));

  if (accessToken === '') accessToken = await createTestBearerToken(); // eslint-disable-line

  setAttributes({
    id: 'consents',
    'access-token': accessToken,
    'lc-id': lcId,
    'api-url': apiUrl,
    theme,
    traceid,
    mode,
  });

  // Do not add empty sp-id or lc-id for piranha test token since
  // that token contains "customer" information with sp-id and lc-id.
  if (spId !== '' && !isPiranhaToken(accessToken)) {
    setAttributes({ 'sp-id': spId });
  }

  if (lcId !== '' && !isPiranhaToken(accessToken)) {
    setAttributes({ 'sp-id': spId });
  }

  if (theme === 'occl') {
    setAttributes({ 'theme-base-url': themeBaseUrl });
  }

  document.getElementById('mf')?.replaceChildren(mf);
};

/**
 * The VUE app managing the testpage interaction
 */
const TestApp = defineComponent({
  components,

  data() {
    return {
      // definitions
      shortcuts,
      // attributes
      ...params,
      // runtime props
      ready: false,
      info: 'Messages:',
      error: false as boolean | string | Error,
    };
  },

  async mounted() {
    this.init();

    if (this.theme === 'occl') {
    // Load external resources into shadowDOM
      injectExternalResources({
        config,
        parentElement: document.querySelector('head')!,
        theme: this.theme as TemplateTheme,
        themeBaseUrl: this.themeBaseUrl,
      });
    }

    await this.create();
  },

  methods: {
    async loadCSS(cssURL: string) {
      return new Promise<void>((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssURL;
        link.onload = () => {
          resolve();
          console.log(`CSS ${cssURL} has loaded!`);
        };
        document.head.appendChild(link);
      });
    },

    async loadTefOne(sp: string, tefOneEnv: string) {
      await this.loadCSS(`https://one.o9.de/${tefOneEnv}/${sp}/variables.css`);
      await this.loadCSS(`https://one.o9.de/${tefOneEnv}/${sp}/components.css`);

      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://one.o9.de/${tefOneEnv}/bundle.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load bundle.js'));
        document.body.appendChild(script);
      });

      console.log(`TefOne for ${sp} on ${tefOneEnv} has loaded!`);
    },

    init() {
      const componentInstance = this; // eslint-disable-line
      document.addEventListener(config.componentTagName, (event: Event) => {
        const { detail } = event as TefComs.Event;
        const { name, level, error } = detail ?? {};
        console.log(`[${config.componentTagName}] Received '%s' event with details %o`, name, detail);
        switch (name) {
          case 'load/READY':
            componentInstance.ready = true;
            componentInstance.setInfo('load/READY: The micro-frontend is ready.');
            break;
          case 'consents/EMPTY':
            componentInstance.setInfo('consents/EMPTY: No consents found for this customer');
            break;
          case 'consents/SAVE':
            componentInstance.setInfo('consents/SAVE: Consents saved successfully');
            break;
          case 'consents/LOAD':
            componentInstance.setInfo('consents/LOAD: Consents successfully loaded');
            break;
          case 'consents/CHANGE':
            componentInstance.setInfo('consents/CHANGE: Consent changed');
            break;
          case 'action/CHANGE':
            componentInstance.setInfo('action/CHANGE: "Ändern" button pressed');
            break;
          case 'action/CANCEL_MICROFRONTEND':
            componentInstance.setInfo('action/CANCEL_MICROFRONTEND: "Zurück" button pressed');
            break;
          default:
        }

        if (level === 'error' || error) {
          componentInstance.setError(error ?? detail.message ?? 'an error occurred');
        }
      });
    },

    reload() {
      const query = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        // @ts-expect-error "for testing purposes"
        query.set(key, this.$data[key] as string ?? '');
      });
      window.location.href = `${window.location.pathname}?${query.toString()}`; // reload page
    },

    async create() {
      try {
        if (this.tefOne) {
          await this.loadTefOne(this.tefOne, this.tefOneEnv);
        }
        await createMicroFrontend(this);
      } catch (error) {
        this.setError(error as Error);
      }
    },

    showHtml() {
      const escapeHtml = (unsafe: string) => unsafe
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      const html = document.getElementById('consents')?.outerHTML;
      this.setInfo(html ? escapeHtml(html) : 'Element not found.');
    },

    async setTestToken() {
      if (!isCorrectUrlForWebCrypto()) {
        this.setError('Creating a token only works within a secure context. Please use "localhost" as a hostname or https to access this page.');
        return;
      }
      this.accessToken = await createTestBearerToken();
      this.setInfo('Created a RAITT token');
    },

    async setTestPiranhaToken() {
      if (!isCorrectUrlForWebCrypto()) {
        this.setError('Creating a token only works within a secure context. Please use "localhost" as a hostname or https to access this page.');
        return;
      }

      this.accessToken = await createTestPiranhaToken(this.spId, this.lcId);
      this.setInfo(`Created a piranha token with credentials spId=${this.spId}, lcId=${this.lcId}`);
    },

    setInfo(text: string) {
      this.info = [this.info].concat(text).join('<br />');
      this.error = false;
    },

    setError(error: Error | string) {
      this.error = error;
      if (error instanceof Error) {
        const { message, stack } = error;
        const stackText = stack ? `<br />${stack}` : '';
        this.info = `${message}${stackText}`;
      } else {
        this.info = error;
      }
    },

    setValue<T = unknown>(prop: string, value: T) {
      // @ts-expect-error "for testing purposes"
      this.$data[prop] = value;
    },
  },
});

const app = createApp(TestApp);
app.mount('#app');
